import React, {memo, Suspense} from "react"
import {MANIFEST} from "@manifest/header"
import {useTemplatedComponent} from "@next/templating"
import {breakpoints} from "@next/ux-fabric"
import {useMediaQuery} from "../../hooks/useMediaQuery"
import {TERTIARY_LINK} from "../../config/constants"
import CatalogueListTitleContent from "../CatalogueListTitleContent"
import CatalogueItem from "../CatalogueItem"
import {Container, StyledAccordionSummary, StyledAccordionDetails, StyledAccordion, Button} from "./component"
import SnailItemIcon from "../SnailItemIcon"
import connect from "./connect"
import {Category, Icons, Link} from "../../models/secondary-nav"
import NavIcon from "../NavIcon"
import urls from "../../config/urls"

type CatalogueMarketingStyles = {
    linkColour: string
    fontWeight: string
}

type CatalogueListTitleProps = {
    catalogueMarketingStyles: CatalogueMarketingStyles
    title: string | null
    icons?: Icons[] | undefined
    isIconEnabled: boolean
    isFirstElement?: boolean
    realm: string
}
export const CatalogueListTitle = memo(
    ({title, icons, isIconEnabled, catalogueMarketingStyles, isFirstElement, realm}: CatalogueListTitleProps) => {
        if (!title) return null
        return (
            <CatalogueListTitleContent
                catalogueMarketingStyles={catalogueMarketingStyles}
                isFirstElement={isFirstElement}
            >
                {isIconEnabled && <SnailItemIcon icons={icons} realm={realm} />} <span> {title} </span>
            </CatalogueListTitleContent>
        )
    },
)
CatalogueListTitle.displayName = "CatalogueListTitle"

type CatalogueListContentProps = {
    items: Link[]
    catalogueListTitle: string | null
    department: string
    tab: string | null
    columnIndex: number | undefined
    isIconEnabled: boolean
    icons?: Icons[] | undefined
    catalogueIndex?: number | undefined
    testingVariant: string | null
    tabEnglish: string | null | undefined
    englishCatalogueListTitle: string | null | undefined
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
}

export const CatalogueListContent = memo(
    ({
        items,
        catalogueListTitle,
        englishCatalogueListTitle,
        department,
        tab,
        tabEnglish,
        columnIndex,
        isIconEnabled,
        icons,
        catalogueIndex,
        testingVariant,
        templating,
    }: CatalogueListContentProps) => {
        const isDirectLink = !catalogueListTitle && columnIndex === 0
        const TemplatedList = useTemplatedComponent(templating, MANIFEST)
        return (
            <Suspense>
                <TemplatedList data-testid="catalogue-items" isDirectLink={isDirectLink}>
                    {items.map(
                        ({title, target, linkColour, fontWeight, icon: promotionalIcon, englishTitle}, index) => {
                            const marketingstyles = {
                                linkColour,
                                fontWeight,
                            }
                            const isFirstElement =
                                !isDirectLink && !catalogueListTitle && catalogueIndex === 0 && index === 0
                            const key = `${title}-${target}`
                            return (
                                <CatalogueItem
                                    data-testid="catalogue-item"
                                    department={department}
                                    tab={tab}
                                    tabEnglish={tabEnglish}
                                    catalogueListTitle={catalogueListTitle}
                                    title={title}
                                    englishTitle={englishTitle}
                                    target={target}
                                    key={key}
                                    icons={icons}
                                    columnIndex={columnIndex}
                                    isIconEnabled={isIconEnabled}
                                    {...marketingstyles}
                                    promotionalIcon={promotionalIcon}
                                    isFirstElement={isFirstElement}
                                    testingVariant={testingVariant}
                                    englishCatalogueListTitle={englishCatalogueListTitle}
                                    isDirectLink={isDirectLink}
                                />
                            )
                        },
                    )}
                </TemplatedList>
            </Suspense>
        )
    },
)
CatalogueListContent.displayName = "CatalogueListContent"

type SmallScreenProps = {
    items: Link[]
    title: string | null
    englishTitle: string | null | undefined
    icons: Icons[] | undefined
    opened?: boolean
    isImagePlaceholderEnabled: boolean
    setOpened: (title: string | null) => void
    catalogueMarketingStyles: CatalogueMarketingStyles
    department: string
    tab: string | null
    tabEnglish: string | null | undefined
    text: string
    columnIndex: number | undefined
    realm: string
    testingVariant: string | null
    variant: string
    iconsVersionPath: string
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    type: string
    setSlideOpened: (isOpened: boolean, catalogueIndex: number) => void
    catalogueIndex: number
}

export const SmallScreen = memo(
    ({
        icons,
        title,
        englishTitle,
        items,
        isImagePlaceholderEnabled,
        catalogueMarketingStyles,
        opened,
        setOpened,
        department,
        tab,
        tabEnglish,
        text,
        columnIndex,
        realm,
        testingVariant,
        variant,
        iconsVersionPath,
        templating,
        type,
        setSlideOpened,
        catalogueIndex,
    }: SmallScreenProps) => {
        const handleChange = (_event: React.ChangeEvent<any>) => {
            setOpened(title)
        }
        if (title && items.length > 0 && items[0].type !== TERTIARY_LINK) {
            return (
                <StyledAccordion square expanded={opened} onChange={handleChange} data-testid="catalogue-accordion">
                    <StyledAccordionSummary
                        expandIcon={
                            <NavIcon
                                imageUrl={urls.burgerMenu.chevronUrl(realm, variant, iconsVersionPath)}
                                width={1.5}
                                altText={text}
                            />
                        }
                    >
                        <CatalogueListTitle
                            title={title}
                            catalogueMarketingStyles={catalogueMarketingStyles}
                            icons={icons}
                            isIconEnabled={isImagePlaceholderEnabled}
                            realm={realm}
                        />
                    </StyledAccordionSummary>

                    <StyledAccordionDetails>
                        <CatalogueListContent
                            department={department}
                            tab={tab}
                            tabEnglish={tabEnglish}
                            catalogueListTitle={title}
                            englishCatalogueListTitle={englishTitle}
                            items={items}
                            columnIndex={columnIndex}
                            isIconEnabled={isImagePlaceholderEnabled}
                            testingVariant={testingVariant}
                            templating={templating}
                        />
                    </StyledAccordionDetails>
                </StyledAccordion>
            )
        }

        if (!title && items.length > 0 && items[0].type === TERTIARY_LINK) {
            return (
                <Button
                    type="button"
                    style={{width: "100%"}}
                    onClick={() => setSlideOpened(true, catalogueIndex)}
                    data-testid="tertiary-link-button"
                >
                    <StyledAccordionSummary
                        expandIcon={
                            <NavIcon
                                imageUrl={urls.burgerMenu.chevronUrl(realm, variant, iconsVersionPath)}
                                width={1.5}
                                altText={text}
                                rotationDegrees={270}
                            />
                        }
                    >
                        <CatalogueListTitle
                            title={items[0].title}
                            catalogueMarketingStyles={catalogueMarketingStyles}
                            icons={icons}
                            isIconEnabled={isImagePlaceholderEnabled}
                            realm={realm}
                        />
                    </StyledAccordionSummary>
                </Button>
            )
        }

        return (
            <CatalogueListContent
                department={department}
                tab={tab}
                tabEnglish={tabEnglish}
                englishCatalogueListTitle={englishTitle}
                catalogueListTitle={title}
                items={items}
                columnIndex={columnIndex}
                isIconEnabled={isImagePlaceholderEnabled}
                icons={icons}
                testingVariant={testingVariant}
                templating={templating}
            />
        )
    },
)
SmallScreen.displayName = "SmallScreen"

type BigScreenProps = {
    items: Link[]
    title: string | null
    englishTitle: string | null | undefined
    catalogueMarketingStyles: CatalogueMarketingStyles
    department: string
    tab: string | null
    tabEnglish: string | null | undefined
    columnIndex: number
    catalogueIndex: number
    realm: string
    testingVariant: string | null
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
}
export const BigScreen = memo(
    ({
        title,
        englishTitle,
        catalogueMarketingStyles,
        items,
        department,
        tab,
        tabEnglish,
        columnIndex,
        catalogueIndex,
        realm,
        testingVariant,
        templating,
    }: BigScreenProps) => {
        return (
            <>
                {title && (
                    <CatalogueListTitle
                        catalogueMarketingStyles={catalogueMarketingStyles}
                        title={title}
                        isIconEnabled={false}
                        isFirstElement={catalogueIndex === 0}
                        realm={realm}
                    />
                )}
                <CatalogueListContent
                    department={department}
                    tab={tab}
                    tabEnglish={tabEnglish}
                    catalogueListTitle={title}
                    englishCatalogueListTitle={englishTitle}
                    items={items}
                    columnIndex={columnIndex}
                    isIconEnabled={false}
                    catalogueIndex={catalogueIndex}
                    testingVariant={testingVariant}
                    templating={templating}
                />
            </>
        )
    },
)
BigScreen.displayName = "BigScreen"

type CatalogueListProps = Category & {
    opened?: boolean
    isImagePlaceholderEnabled: boolean
    setOpened: (title: string | null) => void
    englishTitle: string | null | undefined
    department: string
    tab: string | null
    tabEnglish: string | null | undefined
    text: string
    columnIndex: number
    catalogueIndex: number
    realm: string
    /**
     * A/B Testing variant value. Default variant = null
     */
    testingVariant: string | null
    variant: string
    iconsVersionPath: string
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    type: string
    setSlideOpened: (isOpened: boolean, catalogueIndex: number) => void
}

export const CatalogueList = memo(
    ({
        icons,
        items,
        title,
        englishTitle,
        linkColour,
        fontWeight,
        opened = false,
        setOpened,
        department,
        tab,
        tabEnglish,
        isImagePlaceholderEnabled,
        text,
        columnIndex,
        catalogueIndex,
        realm,
        testingVariant,
        variant,
        iconsVersionPath,
        templating,
        type,
        setSlideOpened,
    }: CatalogueListProps) => {
        const props = {
            catalogueMarketingStyles: {
                linkColour,
                fontWeight,
            },
            icons,
            items,
            title,
            englishTitle,
            linkColour,
            fontWeight,
            opened,
            setOpened,
            department,
            tab,
            tabEnglish,
            isImagePlaceholderEnabled,
            text,
            columnIndex,
            catalogueIndex,
            realm,
            testingVariant,
            variant,
            iconsVersionPath,
            templating,
            type,
            setSlideOpened,
        }

        const smallScreenProps = {...props, text, variant, iconsVersionPath, type, setSlideOpened}

        const isBigScreen = useMediaQuery(`(min-width: ${breakpoints.values.lg}px)`)
        return (
            <Container>
                {isBigScreen ? (
                    <BigScreen {...props} />
                ) : (
                    <SmallScreen {...smallScreenProps} opened={opened} setOpened={setOpened} />
                )}
            </Container>
        )
    },
)
CatalogueList.displayName = "CatalogueList"

export default connect(CatalogueList)
